import { useState, useRef, useEffect } from 'react';

interface EstimateFormProps {
  isOpen: boolean;
  onClose: () => void;
}

const projectTypes = [
  'Hardwood Floor Installation',
  'Hardwood Floor Refinishing',
  'Floor Repair',
  'Floor Replacement',
  'Not Sure — Need Assessment',
];

export default function EstimateForm({ isOpen, onClose }: EstimateFormProps) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const fields = ['name', 'email', 'phone', 'project_type', 'message'];
    fields.forEach((f) => {
      const el = form.elements.namedItem(f) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
      if (el) data.append(f, el.value);
    });
    try {
      await fetch('https://readdy.ai/api/form/d6u30of5ehaffuo434ug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[100] bg-charcoal/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => e.target === overlayRef.current && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-walnut rounded-t-2xl px-8 pt-8 pb-6 relative">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 flex items-center justify-center bg-gold/20 rounded-lg">
              <i className="ri-file-list-3-line text-gold text-xl" />
            </div>
            <span className="text-gold font-sans text-sm font-semibold uppercase tracking-widest">Free Estimate</span>
          </div>
          <h2 className="font-serif text-2xl text-white font-bold">
            Request Your Free In-Home Estimate
          </h2>
          <p className="text-white/70 font-sans text-sm mt-2">
            No obligation. We'll get back to you within 24 hours.
          </p>
        </div>

        <div className="px-8 py-7">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center bg-gold/10 rounded-full mx-auto mb-5">
                <i className="ri-checkbox-circle-line text-gold text-3xl" />
              </div>
              <h3 className="font-serif text-2xl text-walnut font-bold mb-3">
                Request Received!
              </h3>
              <p className="text-charcoal/70 font-sans text-sm leading-relaxed mb-6">
                Thank you for reaching out. Our team will contact you within 24 hours to schedule your free in-home estimate.
              </p>
              <button
                onClick={onClose}
                className="px-8 py-3 bg-walnut text-white rounded-lg font-medium font-sans hover:bg-walnut-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                Close
              </button>
            </div>
          ) : (
            <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-walnut font-sans mb-1.5">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="Your full name"
                  className="w-full px-4 py-3 rounded-lg border border-sand-dark bg-sand text-sm font-sans focus:outline-none focus:border-oak transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-walnut font-sans mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 rounded-lg border border-sand-dark bg-sand text-sm font-sans focus:outline-none focus:border-oak transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-walnut font-sans mb-1.5">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  placeholder="(410) 000-0000"
                  className="w-full px-4 py-3 rounded-lg border border-sand-dark bg-sand text-sm font-sans focus:outline-none focus:border-oak transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-walnut font-sans mb-1.5">
                  Project Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="project_type"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-sand-dark bg-sand text-sm font-sans focus:outline-none focus:border-oak transition-colors appearance-none cursor-pointer"
                >
                  <option value="">Select a service...</option>
                  {projectTypes.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-walnut font-sans mb-1.5">
                  Project Details
                </label>
                <textarea
                  name="message"
                  rows={3}
                  maxLength={500}
                  placeholder="Briefly describe your project (room sizes, floor condition, timeline, etc.)"
                  className="w-full px-4 py-3 rounded-lg border border-sand-dark bg-sand text-sm font-sans focus:outline-none focus:border-oak transition-colors resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gold text-walnut font-semibold rounded-lg hover:bg-gold-dark transition-colors flex items-center justify-center gap-2 font-sans text-sm cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {loading ? (
                  <><i className="ri-loader-4-line animate-spin" /> Sending...</>
                ) : (
                  <><i className="ri-send-plane-line" /> Submit Estimate Request</>
                )}
              </button>
              <p className="text-xs text-center text-charcoal/50 font-sans">
                By submitting, you agree to be contacted regarding your estimate request.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
